/**
 * Página de Status de Processamento
 * Exibe o progresso detalhado da transcrição e análise de arquivos em processamento
 */

import React, { useState, useEffect, useRef } from 'react'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Step,
  StepConnector,
  stepConnectorClasses,
  StepIconProps,
  StepLabel,
  Stepper,
  styled,
  Tooltip,
  Typography,
  keyframes,
  alpha,
  Collapse,
  Divider,
  useMediaQuery,
  useTheme
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import RefreshIcon from '@mui/icons-material/Refresh'
import AudioFileIcon from '@mui/icons-material/AudioFile'
import VideoFileIcon from '@mui/icons-material/VideoFile'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import GraphicEqIcon from '@mui/icons-material/GraphicEq'
import PsychologyIcon from '@mui/icons-material/Psychology'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import SentimentSatisfiedIcon from '@mui/icons-material/SentimentSatisfied'
import LabelIcon from '@mui/icons-material/Label'
import TopicIcon from '@mui/icons-material/Topic'
import TimelineIcon from '@mui/icons-material/Timeline'
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver'
import SummarizeIcon from '@mui/icons-material/Summarize'
import StorageIcon from '@mui/icons-material/Storage'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import DeleteIcon from '@mui/icons-material/Delete'
import ReportProblemIcon from '@mui/icons-material/ReportProblem'
import ReplayIcon from '@mui/icons-material/Replay'
import VisibilityIcon from '@mui/icons-material/Visibility'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import InfoIcon from '@mui/icons-material/Info'
import { FileItem, getAnalysisStatus, AnalysisStatus, retryTranscription, RetryType, getFileStatus } from '../../../services/api'
import BetaBadge from '../../../components/BetaBadge'
import ConfirmRetryDialog from '../../../shared/components/feedback/ConfirmRetryDialog'
import ConfirmDeleteDialog from '../../../shared/components/feedback/ConfirmDeleteDialog'

// Animações
const pulse = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.7; transform: scale(0.95); }
`

const soundWave = keyframes`
  0%, 100% { transform: scaleY(0.3); }
  50% { transform: scaleY(1); }
`

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`

// Animação de reinício elegante
const restartPulse = keyframes`
  0% { 
    opacity: 1; 
    transform: scale(1); 
    box-shadow: 0 0 0 0 rgba(99, 102, 241, 0.4);
  }
  50% { 
    opacity: 0.8; 
    transform: scale(0.98);
    box-shadow: 0 0 0 10px rgba(99, 102, 241, 0);
  }
  100% { 
    opacity: 1; 
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(99, 102, 241, 0);
  }
`

// Animação shimmer (brilho elegante)
const shimmer = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`

const fadeOut = keyframes`
  from { opacity: 1; transform: translateY(0); }
  to { opacity: 0; transform: translateY(-10px); }
`

const slideIn = keyframes`
  from { opacity: 0; transform: translateX(20px); }
  to { opacity: 1; transform: translateX(0); }
`

// Conector customizado do stepper - responsivo
const ColorlibConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 16,
    [theme.breakpoints.up('sm')]: {
      top: 22,
    },
    zIndex: 0, // Linha atrás dos ícones
  },
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundImage: 'linear-gradient(95deg, #6366f1 0%, #8b5cf6 50%, #a855f7 100%)',
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundImage: 'linear-gradient(95deg, #10b981 0%, #34d399 50%, #6ee7b7 100%)',
    },
  },
  [`& .${stepConnectorClasses.line}`]: {
    height: 2,
    border: 0,
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : '#eaeaf0',
    borderRadius: 1,
    [theme.breakpoints.up('sm')]: {
      height: 3,
    },
  },
}))

// Ícone customizado do step - responsivo
const ColorlibStepIconRoot = styled('div')<{
  ownerState: { completed?: boolean; active?: boolean; error?: boolean; disabled?: boolean }
}>(({ theme, ownerState }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[700] : '#ccc',
  zIndex: 2, // Ícones acima da linha
  color: '#fff',
  width: 36,
  height: 36,
  display: 'flex',
  borderRadius: '50%',
  justifyContent: 'center',
  alignItems: 'center',
  boxShadow: '0 2px 6px 0 rgba(0,0,0,.2)',
  transition: 'all 0.3s ease',
  position: 'relative', // Para garantir z-index
  [theme.breakpoints.up('sm')]: {
    width: 50,
    height: 50,
    boxShadow: '0 4px 10px 0 rgba(0,0,0,.25)',
  },
  ...(ownerState.active && {
    backgroundImage: 'linear-gradient(136deg, #6366f1 0%, #8b5cf6 50%, #a855f7 100%)',
    boxShadow: '0 4px 20px 0 rgba(99, 102, 241, 0.5)',
    animation: `${pulse} 2s ease-in-out infinite`,
  }),
  ...(ownerState.completed && {
    backgroundImage: 'linear-gradient(136deg, #10b981 0%, #34d399 50%, #6ee7b7 100%)',
  }),
  ...(ownerState.error && {
    backgroundImage: 'linear-gradient(136deg, #ef4444 0%, #f87171 50%, #fca5a5 100%)',
  }),
  ...(ownerState.disabled && {
    backgroundImage: 'linear-gradient(136deg, #f59e0b 0%, #fbbf24 50%, #fcd34d 100%)',
  }),
}))

function ColorlibStepIcon(props: StepIconProps & { error?: boolean; disabled?: boolean }) {
  const { active, completed, className, icon, error, disabled } = props

  const icons: { [index: string]: React.ReactElement } = {
    1: <HourglassEmptyIcon sx={{ fontSize: { xs: 18, sm: 24 } }} />,
    2: <CloudUploadIcon sx={{ fontSize: { xs: 18, sm: 24 } }} />,
    3: <GraphicEqIcon sx={{ fontSize: { xs: 18, sm: 24 } }} />,
    4: <PsychologyIcon sx={{ fontSize: { xs: 18, sm: 24 } }} />,
    5: <CheckCircleIcon sx={{ fontSize: { xs: 18, sm: 24 } }} />,
  }

  // Quando disabled mas completed, força completed visual
  const effectiveState = {
    completed: disabled ? true : completed,
    active: disabled ? false : active,
    error,
    disabled
  }

  return (
    <ColorlibStepIconRoot ownerState={effectiveState} className={className}>
      {error ? (
        <ErrorIcon sx={{ fontSize: { xs: 18, sm: 24 } }} />
      ) : disabled ? (
        <ReportProblemIcon sx={{ fontSize: { xs: 18, sm: 24 } }} />
      ) : (
        icons[String(icon)]
      )}
    </ColorlibStepIconRoot>
  )
}

// Componente para status individual de análise
type AnalysisItemStatus = 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'DISABLED'

interface AnalysisItemProps {
  label: string
  status: AnalysisItemStatus
  icon: React.ReactNode
  description?: string
}

function AnalysisStatusItem({ label, status, icon, description }: AnalysisItemProps) {
  const isDisabled = status === 'DISABLED'

  const getStatusColor = () => {
    switch (status) {
      case 'COMPLETED': return 'success'
      case 'PROCESSING': return 'primary'
      case 'FAILED': return 'error'
      case 'DISABLED': return 'warning'
      default: return 'default'
    }
  }

  const getStatusLabel = () => {
    switch (status) {
      case 'COMPLETED': return 'OK'
      case 'PROCESSING': return '...'
      case 'FAILED': return 'Erro'
      case 'DISABLED': return 'Desabilitada'
      default: return '—'
    }
  }

  const isProcessing = status === 'PROCESSING'

  const paperContent = (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 1.5, sm: 2.5 },
        borderRadius: 2,
        border: '1px solid',
        borderColor: isDisabled
          ? alpha('#f59e0b', 0.3)
          : status === 'PROCESSING' ? 'primary.main' : 'divider',
        bgcolor: isDisabled
          ? alpha('#fef3c7', 0.3)
          : status === 'PROCESSING' ? alpha('#6366f1', 0.05) : 'background.paper',
        transition: 'all 0.3s ease',
        opacity: isDisabled ? 0.75 : 1,
        ...(isDisabled ? {
          backgroundImage: `repeating-linear-gradient(
            -45deg,
            transparent,
            transparent 10px,
            ${alpha('#f59e0b', 0.04)} 10px,
            ${alpha('#f59e0b', 0.04)} 20px
          )`
        } : {}),
        ...(!isDisabled && {
          '&:hover': {
            borderColor: 'primary.light',
            boxShadow: 1
          }
        })
      }}
    >
      <Stack direction="row" spacing={{ xs: 1, sm: 2 }} alignItems="center">
        <Box
          sx={{
            width: { xs: 36, sm: 48 },
            height: { xs: 36, sm: 48 },
            borderRadius: { xs: 1.5, sm: 2 },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            bgcolor: isDisabled
              ? alpha('#f59e0b', 0.1)
              : status === 'COMPLETED'
                ? alpha('#10b981', 0.1)
                : status === 'PROCESSING'
                  ? alpha('#6366f1', 0.1)
                  : status === 'FAILED'
                    ? alpha('#ef4444', 0.1)
                    : alpha('#9ca3af', 0.1),
            color: isDisabled
              ? '#f59e0b'
              : status === 'COMPLETED'
                ? 'success.main'
                : status === 'PROCESSING'
                  ? 'primary.main'
                  : status === 'FAILED'
                    ? 'error.main'
                    : 'text.secondary',
            '& svg': {
              fontSize: { xs: 18, sm: 24 }
            },
            ...(isProcessing && {
              animation: `${pulse} 1.5s ease-in-out infinite`
            })
          }}
        >
          {isDisabled ? (
            <ReportProblemIcon sx={{ fontSize: { xs: 18, sm: 24 } }} />
          ) : isProcessing ? (
            <CircularProgress size={18} color="inherit" sx={{ '&&': { width: { xs: 16, sm: 24 }, height: { xs: 16, sm: 24 } } }} />
          ) : (
            icon
          )}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="subtitle1"
            fontWeight={600}
            sx={{
              fontSize: { xs: '0.8rem', sm: '1rem' },
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              color: isDisabled ? 'text.secondary' : 'text.primary'
            }}
          >
            {label}
          </Typography>
          {description && (
            <Typography
              variant="body2"
              color={isDisabled ? 'text.disabled' : 'text.secondary'}
              sx={{
                fontSize: { xs: '0.65rem', sm: '0.875rem' },
                display: { xs: 'none', sm: 'block' },
                ...(isDisabled && { fontStyle: 'italic' })
              }}
            >
              {isDisabled ? 'Não incluída nesta transcrição' : description}
            </Typography>
          )}
        </Box>
        <Chip
          size="small"
          label={getStatusLabel()}
          color={getStatusColor()}
          variant={status === 'PROCESSING' ? 'filled' : 'outlined'}
          sx={{
            fontWeight: 500,
            minWidth: { xs: 40, sm: 'auto' },
            height: { xs: 22, sm: 24 },
            fontSize: { xs: '0.65rem', sm: '0.8125rem' },
            flexShrink: 0,
            ...(isProcessing && {
              animation: `${pulse} 2s ease-in-out infinite`
            })
          }}
        />
      </Stack>
    </Paper>
  )

  // Se é desabilitada, envolve com Tooltip
  if (isDisabled) {
    return (
      <Tooltip
        title="Esta análise foi desabilitada. Você poderá executá-la posteriormente, após a conclusão da transcrição."
        arrow
        placement="left"
      >
        {paperContent}
      </Tooltip>
    )
  }

  return paperContent
}

// Formatadores
function formatBytes(bytes: number): string {
  if (!bytes) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
}

function formatEstimatedTime(seconds: number | null | undefined): string {
  if (!seconds || seconds <= 0) return 'Calculando...'
  
  if (seconds < 60) {
    return `~${seconds} segundos`
  }
  
  const minutes = Math.ceil(seconds / 60)
  if (minutes === 1) {
    return '~1 minuto'
  }
  
  return `~${minutes} minutos`
}

function formatDate(timestamp: number | string | null | undefined): string {
  if (timestamp === null || timestamp === undefined || timestamp === '') return '—'

  let date: Date

  if (typeof timestamp === 'number') {
    // Se vier em segundos (ex: 1700000000), converter para ms
    const ms = timestamp < 1e12 ? timestamp * 1000 : timestamp
    date = new Date(ms)
  } else {
    // Se string numérica, tentar converter
    const numeric = Number(timestamp)
    if (!Number.isNaN(numeric)) {
      const ms = numeric < 1e12 ? numeric * 1000 : numeric
      date = new Date(ms)
    } else {
      date = new Date(timestamp)
    }
  }

  if (Number.isNaN(date.getTime())) return '—'

  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

function getStepIndex(state: string | null | undefined): number {
  switch (state) {
    case 'PENDING': return 0
    case 'UPLOADING': return 1
    case 'TRANSCRIBING': return 2
    case 'PROCESSING_TRANSCRIPTION': return 2
    case 'ANALYZING': return 3
    case 'COMPLETED': return 4
    case 'FAILED': return -1
    case 'WARNING': return -1
    default: return 0
  }
}

function getStateDescription(state: string | null | undefined, message?: string | null): string {
  if (message) return message
  switch (state) {
    case 'PENDING': return 'Aguardando na fila para processamento...'
    case 'UPLOADING': return 'Enviando arquivo para o servidor...'
    case 'TRANSCRIBING': return 'Convertendo áudio em texto com IA...'
    case 'PROCESSING_TRANSCRIPTION': return 'Processando resultado da transcrição...'
    case 'ANALYZING': return 'Analisando transcrição com inteligência artificial...'
    case 'COMPLETED': return 'Processamento concluído com sucesso!'
    case 'FAILED': return 'Ocorreu um erro durante o processamento.'
    case 'WARNING': return 'Arquivo não elegível para transcrição.'
    default: return 'Processando...'
  }
}

/**
 * Retorna a mensagem amigável para o usuário a partir do código de erro do backend.
 * Cobre os códigos produzidos pelas Abordagens A/B/C/D da pipeline Fireflies.
 */
function getErrorCodeMessage(errorCode: string | null | undefined): { title: string; detail: string; unrecoverable: boolean } | null {
  switch (errorCode) {
    case 'NO_AUDIO_DETECTED':
      return {
        title: 'Arquivo sem faixa de áudio',
        detail: 'Este vídeo não contém uma faixa de áudio com diálogos. Certifique-se de que o arquivo possui áudio antes de reenviá-lo.',
        unrecoverable: true
      }
    case 'EMPTY_TRANSCRIPT':
      return {
        title: 'Nenhuma fala detectada',
        detail: 'O arquivo foi processado, mas nenhuma fala foi identificada. O arquivo pode conter áudio sem voz, música ou ruído de fundo sem diálogos audivíveis.',
        unrecoverable: true
      }
    case 'FIREFLIES_ERROR':
      return {
        title: 'Erro no motor de transcrição',
        detail: 'O motor de transcrição encontrou um erro ao processar este arquivo. Você pode tentar reenviar o arquivo.',
        unrecoverable: false
      }
    case 'FIREFLIES_TIMEOUT':
      return {
        title: 'Tempo limite de transcrição excedido',
        detail: 'O motor de transcrição demorou mais do que o esperado e o processamento foi cancelado. Isso pode ocorrer com arquivos muito longos ou com qualidade de áudio baixa.',
        unrecoverable: false
      }
    case 'UPLOAD_FAILED':
      return {
        title: 'Falha no envio do arquivo',
        detail: 'Não foi possível enviar o arquivo para processamento. Verifique sua conexão e tente novamente.',
        unrecoverable: false
      }
    default:
      return null
  }
}

// Props do componente
type Props = {
  file: FileItem
  onBack: () => void
  onRefresh?: () => void
  onDelete?: (fileId: string) => void
  onOpenAnalysis?: () => void
}

export default function ProcessingStatusPage({ file: initialFile, onBack, onRefresh, onDelete, onOpenAnalysis }: Props) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const isTablet = useMediaQuery(theme.breakpoints.down('md'))

  const [file, setFile] = useState<FileItem>(initialFile)
  const [analysisStatus, setAnalysisStatus] = useState<AnalysisStatus | null>(null)
  const [loading, setLoading] = useState(false)
  const [showDetails, setShowDetails] = useState(true)
  const [retrying, setRetrying] = useState(false)
  const [retrySuccess, setRetrySuccess] = useState(false)
  const [retryMessage, setRetryMessage] = useState<string | null>(null)
  const [retryError, setRetryError] = useState<string | null>(null)
  const [currentTime, setCurrentTime] = useState(Date.now())
  const [showRetryDialog, setShowRetryDialog] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const filePollingRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const processingState = file?.processingState || file?.currentState || 'PENDING'
  const stepIndex = getStepIndex(processingState)
  const isFailed = processingState === 'FAILED'
  const isWarning = processingState === 'WARNING'
  const isCompleted = processingState === 'COMPLETED'
  const serverProgress = file?.progress ?? 0

  // Informações de erro detalhadas
  const errorCode = file?.errorCode
  const errorInfo = getErrorCodeMessage(errorCode)
  const isUnrecoverableError = errorInfo?.unrecoverable === true
  
  // Progresso interpolado localmente para animação suave entre pollings
  const [interpolatedProgress, setInterpolatedProgress] = useState(serverProgress)
  const interpolationRef = useRef<ReturnType<typeof setInterval> | null>(null)
  
  /**
   * Calcula perturbação pseudo-aleatória para variar o progresso de forma realista.
   * Usa hash do fileId para criar um padrão único por arquivo.
   * Simula variações reais de processamento (aceleração, desaceleração, processamento em lotes).
   */
  const calculatePerturbation = (fileId: string | undefined, currentTimeMs: number, maxPerturbation: number): number => {
    if (!fileId) return 0
    
    // Hash determinístico baseado no fileId
    let hash = 0
    for (let i = 0; i < fileId.length; i++) {
      const char = fileId.charCodeAt(i)
      hash = ((hash << 5) - hash) + char
      hash = hash & hash // Converter para 32-bit
    }
    
    // Gerar padrões oscilatórios realistas usando múltiplas ondas
    const wave1 = Math.sin((currentTimeMs % 15000) / 2387.3) * 0.6  // onda lenta (15s período)
    const wave2 = Math.sin((currentTimeMs % 5000) / 795.8) * 0.3    // onda rápida (5s período)
    const wave3 = Math.sin((currentTimeMs % 1000) / 159.2) * 0.1    // ruído fino (1s período)
    
    // Combinar ondas com seed do arquivo
    const seed = Math.abs(hash % 1000) / 2000.0
    const oscillation = (wave1 + wave2 + wave3) * (0.5 + seed)
    
    // Adicionar pulsos ocasionais (processamento em chunks)
    const pulsePhase = Math.floor((currentTimeMs / 1000) % 7)
    const pulse = pulsePhase < 2 ? 0.3 : -0.2 // 2s de +30%, 5s de -20%
    
    // Combinar tudo
    const perturbation = (oscillation * 0.7 + pulse * 0.3) * maxPerturbation
    
    return Math.max(-maxPerturbation, Math.min(maxPerturbation, perturbation))
  }
  
  // Interpolar progresso entre pollings (atualiza a cada 1s)
  useEffect(() => {
    if (isCompleted || isFailed || isWarning) {
      setInterpolatedProgress(serverProgress)
      return
    }
    
    // Atualizar para o valor do servidor imediatamente
    setInterpolatedProgress(prev => Math.max(prev, serverProgress))
    
    // Se tem dados de tempo estimado, interpolar entre pollings
    if (file?.processingStartedAt && file?.estimatedCompletionAt && file?.fileId) {
      if (interpolationRef.current) clearInterval(interpolationRef.current)
      
      interpolationRef.current = setInterval(() => {
        const now = Date.now()
        const started = file.processingStartedAt!
        const estimated = file.estimatedCompletionAt!
        const totalMs = estimated - started
        const elapsedMs = now - started
        
        if (totalMs <= 0) return
        
        const ratio = Math.min(elapsedMs / totalMs, 0.95)
        
        let baseProgress: number
        if (processingState === 'TRANSCRIBING') {
          // Range 30-49%
          baseProgress = 30 + ratio * 19
        } else if (processingState === 'ANALYZING') {
          // Range 70-94%
          baseProgress = 70 + ratio * 24
        } else {
          baseProgress = serverProgress
        }
        
        // Adicionar perturbação realista
        const perturbation = calculatePerturbation(file.fileId, now, processingState === 'TRANSCRIBING' ? 3 : 2.5)
        const calculated = processingState === 'TRANSCRIBING' 
          ? Math.max(30, Math.min(49, baseProgress + perturbation))
          : Math.max(70, Math.min(94, baseProgress + perturbation))
        
        setInterpolatedProgress(prev => Math.max(prev, Math.round(calculated)))
      }, 1000)
      
      return () => {
        if (interpolationRef.current) {
          clearInterval(interpolationRef.current)
          interpolationRef.current = null
        }
      }
    }
  }, [serverProgress, file?.processingStartedAt, file?.estimatedCompletionAt, processingState, isCompleted, isFailed, isWarning])
  
  const progress = isCompleted ? 100 : interpolatedProgress

  // Timeouts em minutos
  const TRANSCRIPTION_TIMEOUT_MINUTES = 30 // 30 minutos para transcrição
  const ANALYSIS_TIMEOUT_MINUTES = 15 // 15 minutos para análise

  // Determinar se a transcrição já foi completada (arquivo no S3)
  // Se transcriptionCompletedAt existe OU se o estado é ANALYZING ou posterior, a transcrição existe
  const hasTranscription = Boolean(
    file?.transcriptionCompletedAt || 
    processingState === 'ANALYZING' ||
    processingState === 'COMPLETED'
  )

  // Calcular tempo decorrido desde o início do processamento
  const processingStartedAt = file?.processingStartedAt
  const transcriptionCompletedAt = file?.transcriptionCompletedAt
  
  // Tempo decorrido em minutos
  const minutesSinceStart = processingStartedAt 
    ? Math.floor((currentTime - processingStartedAt) / (1000 * 60)) 
    : 0
  
  const minutesSinceTranscription = transcriptionCompletedAt 
    ? Math.floor((currentTime - transcriptionCompletedAt) / (1000 * 60)) 
    : 0

  // Verificar se está demorando muito
  const isTranscriptionTimeout = !hasTranscription && 
    !isFailed && 
    !isCompleted && 
    minutesSinceStart > TRANSCRIPTION_TIMEOUT_MINUTES

  const isAnalysisTimeout = hasTranscription && 
    !isFailed && 
    !isCompleted && 
    minutesSinceTranscription > ANALYSIS_TIMEOUT_MINUTES

  const isTimeout = isTranscriptionTimeout || isAnalysisTimeout

  // Mostrar botão de retry se: falhou OU timeout, MAS NÃO para erros irrecuperáveis ou WARNING
  const showRetryOption = (isFailed || isTimeout) && !isUnrecoverableError && !isWarning

  // Determinar automaticamente o tipo de retry baseado no estado
  // Se tem transcrição → retry de análise; caso contrário → retry de arquivo
  const autoRetryType: RetryType = hasTranscription ? 'transcription' : 'file'

  const FileIcon = file?.type?.toLowerCase().includes('video') ? VideoFileIcon : AudioFileIcon

  // Atualizar currentTime a cada minuto para verificar timeouts
  useEffect(() => {
    if (isCompleted || isFailed || isWarning) return
    
    const timer = setInterval(() => {
      setCurrentTime(Date.now())
    }, 60000) // Atualiza a cada minuto
    
    return () => clearInterval(timer)
  }, [isCompleted, isFailed, isWarning])

  // Polling para status de análise detalhado
  useEffect(() => {
    if (!file?.fileId || isCompleted || isFailed) return

    const fetchStatus = async () => {
      try {
        const status = await getAnalysisStatus(file.fileId)
        setAnalysisStatus(status)
      } catch (e) {
        console.warn('Erro ao buscar status de análise:', e)
      }
    }

    // Busca inicial
    fetchStatus()

    // Polling a cada 3 segundos
    pollingRef.current = setInterval(fetchStatus, 3000)

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current)
        pollingRef.current = null
      }
    }
  }, [file?.fileId, isCompleted, isFailed, isWarning])

  // Polling para atualizar o arquivo completo (estado/progresso)
  // Usa endpoint específico do arquivo em vez de recarregar toda a lista
  useEffect(() => {
    if (!file?.fileId || isCompleted || isFailed || isWarning) return

    const refreshFile = async () => {
      try {
        const updatedFile = await getFileStatus(file.fileId)
        setFile(updatedFile)
      } catch (e) {
        console.warn('Erro ao atualizar status do arquivo:', e)
      }
    }

    // Busca inicial
    refreshFile()

    // Atualiza a cada 5 segundos
    filePollingRef.current = setInterval(refreshFile, 5000)

    return () => {
      if (filePollingRef.current) {
        clearInterval(filePollingRef.current)
        filePollingRef.current = null
      }
    }
  }, [file?.fileId, isCompleted, isFailed, isWarning])

  const handleRefresh = () => {
    if (onRefresh) {
      setLoading(true)
      onRefresh()
      setTimeout(() => setLoading(false), 500)
    }
  }

  /**
   * Abre o modal de confirmação de exclusão
   */
  const handleDelete = () => {
    if (!isDeleting) {
      setShowDeleteDialog(true)
    }
  }

  /**
   * Executa a exclusão após confirmação no modal
   */
  const handleConfirmDelete = async () => {
    if (!onDelete) return
    
    setIsDeleting(true)
    
    try {
      await onDelete(file.fileId)
      
      // Aguarda feedback visual antes de voltar
      await new Promise(resolve => setTimeout(resolve, 500))
      onBack()
    } catch (error) {
      console.error('Erro ao excluir arquivo:', error)
    } finally {
      setIsDeleting(false)
      setShowDeleteDialog(false)
    }
  }

  /**
   * Abre o modal de confirmação de retry
   */
  const handleRetry = () => {
    if (!retrying) {
      setShowRetryDialog(true)
    }
  }

  /**
   * Executa o retry após confirmação no modal
   */
  const handleConfirmRetry = async () => {
    setShowRetryDialog(false)
    setRetrying(true)
    setRetryMessage(null)
    setRetryError(null)
    setRetrySuccess(false)
    
    try {
      const result = await retryTranscription(file.fileId, autoRetryType)
      
      // Sucesso - mostrar mensagem e iniciar transição visual
      setRetrySuccess(true)
      setRetryMessage(result.message)
      
      // Atualizar estado local imediatamente para feedback visual
      setFile(prev => ({
        ...prev,
        processingState: result.processingState as any,
        status: result.status,
        progress: 0,
        message: result.message
      }))
      
      // Limpar status de análise para mostrar como "reiniciando"
      setAnalysisStatus(null)
      
      // Após pequeno delay, atualizar a lista
      setTimeout(() => {
        if (onRefresh) onRefresh()
        // Limpar estado de sucesso após transição
        setTimeout(() => {
          setRetrySuccess(false)
          setRetryMessage(null)
        }, 2000)
      }, 1500)
      
    } catch (error: any) {
      const errorMessage = error.message || 'Falha ao solicitar retry. Tente novamente.'
      setRetryError(errorMessage)
      setRetryMessage(null)
    } finally {
      setRetrying(false)
    }
  }

  // Labels curtos para mobile, completos para desktop
  const steps = isMobile
    ? ['Fila', 'Upload', 'Transcr.', 'Análise', 'OK']
    : ['Aguardando', 'Enviando', 'Transcrevendo', 'Analisando', 'Concluído']

  // Mapear status de análise
  const getAnalysisItemStatus = (status: string | undefined): AnalysisItemStatus => {
    switch (status) {
      case 'COMPLETED': return 'COMPLETED'
      case 'PROCESSING': return 'PROCESSING'
      case 'FAILED': return 'FAILED'
      default: return 'PENDING'
    }
  }

  // Verificar se um tipo de análise está habilitado
  const selectedAnalyses = file?.selectedAnalyses
  const hasAnalysisConfig = selectedAnalyses != null && selectedAnalyses.length > 0
  const allAnalysesDisabled = selectedAnalyses != null && selectedAnalyses.length === 0
  
  const isAnalysisEnabled = (type: string): boolean => {
    // Se selectedAnalyses é null/undefined, todas habilitadas (backward compatibility)
    if (selectedAnalyses == null) {
      return true
    }
    // Se é array vazio, todas desabilitadas
    if (selectedAnalyses.length === 0) {
      return false
    }
    // Se tem itens, verifica se o tipo está incluído
    return selectedAnalyses.includes(type)
  }

  const getItemStatus = (type: string, apiStatus: string | undefined): AnalysisItemStatus => {
    const enabled = isAnalysisEnabled(type)
    return enabled ? getAnalysisItemStatus(apiStatus) : 'DISABLED'
  }

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      bgcolor: 'background.default',
      animation: `${fadeIn} 0.3s ease-out`
    }}>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          position: 'sticky',
          top: 0,
          zIndex: 10
        }}
      >
        <Container maxWidth="lg">
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ py: { xs: 1.5, sm: 2 } }}
          >
            {/* Lado esquerdo - Voltar e título */}
            <Stack direction="row" alignItems="center" spacing={{ xs: 1, sm: 2 }} sx={{ minWidth: 0, flex: 1 }}>
              <Tooltip title="Voltar para lista">
                <IconButton
                  onClick={onBack}
                  sx={{
                    bgcolor: 'action.hover',
                    flexShrink: 0,
                    width: { xs: 36, sm: 40 },
                    height: { xs: 36, sm: 40 }
                  }}
                >
                  <ArrowBackIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                </IconButton>
              </Tooltip>

              {/* Ícone do arquivo - escondido em xs para economizar espaço */}
              <Box
                sx={{
                  width: { xs: 36, sm: 48 },
                  height: { xs: 36, sm: 48 },
                  borderRadius: 2,
                  display: { xs: 'none', sm: 'flex' },
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: isFailed ? alpha('#ef4444', 0.1) : alpha('#6366f1', 0.1),
                  color: isFailed ? 'error.main' : 'primary.main',
                  flexShrink: 0
                }}
              >
                <FileIcon sx={{ fontSize: { xs: 20, sm: 28 } }} />
              </Box>

              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography
                    variant="h6"
                    fontWeight={700}
                    sx={{
                      lineHeight: 1.2,
                      fontSize: { xs: '0.95rem', sm: '1.25rem' }
                    }}
                  >
                    {isMobile ? 'Status' : 'Status do Processamento'}
                  </Typography>
                  <BetaBadge />
                </Stack>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    maxWidth: { xs: 180, sm: 300, md: 500 },
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontSize: { xs: '0.75rem', sm: '0.875rem' }
                  }}
                >
                  {file?.title}
                </Typography>
              </Box>
            </Stack>

            {/* Lado direito - Ações */}
            <Stack direction="row" spacing={{ xs: 0.5, sm: 1 }} sx={{ flexShrink: 0 }}>
              {onRefresh && (
                <Tooltip title="Atualizar">
                  <IconButton
                    onClick={handleRefresh}
                    disabled={loading}
                    size={isMobile ? 'small' : 'medium'}
                  >
                    <RefreshIcon sx={{
                      animation: loading ? `${spin} 1s linear infinite` : 'none',
                      fontSize: { xs: 20, sm: 24 }
                    }} />
                  </IconButton>
                </Tooltip>
              )}
              {onDelete && (
                <Tooltip title="Excluir arquivo">
                  <IconButton
                    onClick={handleDelete}
                    color="error"
                    size={isMobile ? 'small' : 'medium'}
                  >
                    <DeleteIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          </Stack>
        </Container>
      </Paper>

      {/* Conteúdo */}
      <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 4 }, px: { xs: 2, sm: 3 } }}>
        
        {/* Stepper de progresso principal */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 4 },
            mb: { xs: 2, sm: 4 },
            borderRadius: { xs: 2, sm: 3 },
            border: '1px solid',
            borderColor: 'divider',
            background: 'linear-gradient(180deg, rgba(99, 102, 241, 0.02) 0%, rgba(139, 92, 246, 0.02) 100%)',
            overflow: 'hidden'
          }}
        >
          <Stepper
            alternativeLabel
            activeStep={(isFailed || isWarning) ? -1 : stepIndex}
            connector={<ColorlibConnector />}
            sx={{
              '& .MuiStepLabel-label': {
                mt: { xs: 0.5, sm: 1 }
              },
              // Força conectores dentro de steps com classe step-force-completed
              // a mostrar a linha verde, independente do activeStep do MUI
              '& .step-force-completed .MuiStepConnector-line': {
                backgroundImage: 'linear-gradient(95deg, #10b981 0%, #34d399 50%, #6ee7b7 100%) !important',
              },
            }}
          >
            {steps.map((label, index) => {
              const isAnalysisStep = index === 3
              const isStepDisabled = isAnalysisStep && allAnalysesDisabled
              
              // Step é completed se:
              // 1. Não falhou E stepIndex já passou ou é o step atual (>=), OU
              // 2. É step de análise E análise está desabilitada E já passou da transcrição (stepIndex > 2)
              const isStepCompleted = !isFailed && !isWarning && (
                stepIndex >= index || 
                (isAnalysisStep && allAnalysesDisabled && stepIndex > 2)
              )
              
              return (
                <Step 
                  key={label} 
                  completed={isStepCompleted}
                  className={isStepCompleted ? 'step-force-completed' : ''}
                >
                  <StepLabel
                    StepIconComponent={(props) => (
                      <ColorlibStepIcon 
                        {...props} 
                        error={(isFailed || isWarning) && stepIndex === -1 && index === 0}
                        disabled={isStepDisabled && !isFailed && !isWarning}
                      />
                    )}
                  >
                    {isStepDisabled ? (
                      <Tooltip
                        title="Esta etapa foi desabilitada. Você poderá executar as análises posteriormente, após a conclusão da transcrição."
                        arrow
                        placement="top"
                      >
                        <Typography
                          variant="body2"
                          fontWeight={stepIndex === index ? 700 : 500}
                          color={stepIndex === index ? 'primary' : 'text.secondary'}
                          sx={{
                            fontSize: { xs: '0.65rem', sm: '0.875rem' },
                            cursor: 'help',
                          }}
                        >
                          {label}
                        </Typography>
                      </Tooltip>
                    ) : (
                      <Typography
                        variant="body2"
                        fontWeight={stepIndex === index ? 700 : 500}
                        color={stepIndex === index ? 'primary' : 'text.secondary'}
                        sx={{
                          fontSize: { xs: '0.65rem', sm: '0.875rem' }
                        }}
                      >
                        {label}
                      </Typography>
                    )}
                  </StepLabel>
                </Step>
              )
            })}
          </Stepper>
        </Paper>

        <Grid container spacing={{ xs: 2, sm: 3 }}>
          {/* Coluna esquerda - Status e progresso */}
          <Grid item xs={12} md={6}>
            {/* Status atual */}
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2, sm: 3 },
                mb: { xs: 2, sm: 3 },
                borderRadius: { xs: 2, sm: 3 },
                border: '1px solid',
                borderColor: isFailed ? 'error.main' : isCompleted ? 'success.main' : 'primary.main'
              }}
              style={{
                backgroundColor: isFailed
                  ? alpha('#ef4444', 0.05)
                  : isCompleted
                    ? alpha('#10b981', 0.05)
                    : alpha('#6366f1', 0.05)
              }}
            >
              <Stack spacing={{ xs: 2, sm: 3 }}>
                {/* Status header - stack vertical em mobile */}
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                  justifyContent="space-between"
                  spacing={{ xs: 1.5, sm: 0 }}
                >
                  <Stack direction="row" alignItems="center" spacing={{ xs: 1.5, sm: 2 }}>
                    {isFailed ? (
                      <ErrorIcon color="error" sx={{ fontSize: { xs: 28, sm: 32 } }} />
                    ) : isWarning ? (
                      <ReportProblemIcon sx={{ fontSize: { xs: 28, sm: 32 }, color: 'warning.main' }} />
                    ) : isCompleted ? (
                      <CheckCircleIcon color="success" sx={{ fontSize: { xs: 28, sm: 32 } }} />
                    ) : (
                      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                        {[0, 1, 2, 3, 4].map((i) => (
                          <Box
                            key={i}
                            sx={{
                              width: { xs: 3, sm: 4 },
                              height: { xs: 18, sm: 24 },
                              bgcolor: 'primary.main',
                              borderRadius: 1,
                              animation: `${soundWave} 0.8s ease-in-out infinite`,
                              animationDelay: `${i * 0.1}s`
                            }}
                          />
                        ))}
                      </Box>
                    )}
                    <Box>
                      <Typography
                        variant="h6"
                        fontWeight={700}
                        sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
                      >
                        {isFailed
                          ? (isMobile ? 'Falhou' : 'Processamento Falhou')
                          : isWarning
                            ? (isMobile ? 'Não elegível' : 'Arquivo Não Elegível')
                            : isCompleted
                              ? (isMobile ? 'Concluído' : 'Processamento Concluído')
                              : (isMobile ? 'Processando' : 'Em Processamento')}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                      >
                        {getStateDescription(processingState, file?.message)}
                      </Typography>
                    </Box>
                  </Stack>

                  {!isCompleted && !isFailed && !isWarning && file?.estimatedSecondsRemaining && (
                    <Chip
                      icon={<AccessTimeIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />}
                      label={formatEstimatedTime(file.estimatedSecondsRemaining)}
                      size="small"
                      variant="outlined"
                      color="primary"
                      sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }}
                    />
                  )}
                </Stack>

                {!isFailed && !isCompleted && !isWarning && (
                  <Box>
                    <Stack direction="row" justifyContent="space-between" mb={1}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                        Progresso geral
                      </Typography>
                      <Typography variant="body2" fontWeight={700} color="primary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                        {progress}%
                      </Typography>
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={progress}
                      sx={{
                        height: { xs: 8, sm: 10 },
                        borderRadius: 5,
                        bgcolor: alpha('#6366f1', 0.1),
                        '& .MuiLinearProgress-bar': {
                          borderRadius: 5,
                          backgroundImage: 'linear-gradient(90deg, #6366f1, #8b5cf6, #a855f7)'
                        }
                      }}
                    />
                  </Box>
                )}
              </Stack>
            </Paper>

            {/* Informações do arquivo */}
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2, sm: 3 },
                borderRadius: { xs: 2, sm: 3 },
                border: '1px solid',
                borderColor: 'divider'
              }}
            >
              <Typography variant="subtitle1" fontWeight={700} mb={{ xs: 1.5, sm: 2 }} sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                {isMobile ? 'Arquivo' : 'Informações do Arquivo'}
              </Typography>
              <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                <Grid item xs={6}>
                  <Stack spacing={0.25}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                      <StorageIcon sx={{ fontSize: { xs: 12, sm: 14 } }} />
                      Tamanho
                    </Typography>
                    <Typography variant="body1" fontWeight={500} sx={{ fontSize: { xs: '0.8rem', sm: '1rem' } }}>
                      {formatBytes(file?.bytes || 0)}
                    </Typography>
                  </Stack>
                </Grid>
                <Grid item xs={6}>
                  <Stack spacing={0.25}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                      <AudioFileIcon sx={{ fontSize: { xs: 12, sm: 14 } }} />
                      Tipo
                    </Typography>
                    <Typography variant="body1" fontWeight={500} sx={{ fontSize: { xs: '0.8rem', sm: '1rem' } }}>
                      {file?.type?.toUpperCase() || 'Áudio'}
                    </Typography>
                  </Stack>
                </Grid>
                <Grid item xs={6}>
                  <Stack spacing={0.25}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                      <AccessTimeIcon sx={{ fontSize: { xs: 12, sm: 14 } }} />
                      Enviado
                    </Typography>
                    <Typography variant="body1" fontWeight={500} sx={{ fontSize: { xs: '0.8rem', sm: '1rem' } }}>
                      {formatDate(file?.createdAt)}
                    </Typography>
                  </Stack>
                </Grid>
                <Grid item xs={6}>
                  <Stack spacing={0.25}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                      <AccessTimeIcon sx={{ fontSize: { xs: 12, sm: 14 } }} />
                      Início
                    </Typography>
                    <Typography variant="body1" fontWeight={500} sx={{ fontSize: { xs: '0.8rem', sm: '1rem' } }}>
                      {formatDate(file?.processingStartedAt)}
                    </Typography>
                  </Stack>
                </Grid>
              </Grid>
            </Paper>

            {/* Painel de WARNING: arquivo não elegível para transcrição */}
            {isWarning && (
              <Paper
                elevation={0}
                sx={{
                  mt: { xs: 2, sm: 3 },
                  p: { xs: 2, sm: 3 },
                  borderRadius: { xs: 2, sm: 3 },
                  border: '1px solid',
                  borderColor: 'warning.main',
                  overflow: 'hidden'
                }}
                style={{ backgroundColor: alpha('#f59e0b', 0.08) }}
              >
                <Stack direction="row" spacing={{ xs: 1.5, sm: 2 }} alignItems="flex-start">
                  <ReportProblemIcon sx={{ mt: 0.25, fontSize: { xs: 24, sm: 28 }, flexShrink: 0, color: 'warning.main' }} />
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="subtitle1" color="warning.dark" fontWeight={700} sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                      {errorInfo ? errorInfo.title : 'Arquivo não elegível para transcrição'}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mt: 0.5,
                        wordBreak: 'break-word',
                        whiteSpace: 'pre-wrap',
                        fontSize: { xs: '0.75rem', sm: '0.875rem' }
                      }}
                    >
                      {errorInfo
                        ? errorInfo.detail
                        : (file?.errorMessage || 'Este arquivo não pode ser transcrito.')}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            )}

            {/* Orientação para arquivo WARNING (inelegível) */}
            {isWarning && (
              <Paper
                elevation={0}
                sx={{
                  mt: { xs: 1.5, sm: 2 },
                  p: { xs: 2, sm: 3 },
                  borderRadius: { xs: 2, sm: 3 },
                  border: '1px solid',
                  borderColor: 'warning.light',
                }}
                style={{ backgroundColor: alpha('#f59e0b', 0.04) }}
              >
                <Stack direction="row" spacing={{ xs: 1.5, sm: 2 }} alignItems="flex-start">
                  <InfoIcon sx={{ mt: 0.25, fontSize: { xs: 20, sm: 24 }, flexShrink: 0, color: 'warning.main' }} />
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="subtitle1" fontWeight={700} color="warning.dark" sx={{ fontSize: { xs: '0.85rem', sm: '0.95rem' } }}>
                      O que fazer?
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      Exclua este arquivo e envie uma nova gravação que contenha diálogos audivíveis. Certifique-se de que o volume está adequado e que não há apenas ruído de fundo.
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            )}

            {/* Mensagem de erro detalhada */}
            {isFailed && (errorInfo || analysisStatus?.errorMessage || file?.errorMessage) && (
              <Paper
                elevation={0}
                sx={{
                  mt: { xs: 2, sm: 3 },
                  p: { xs: 2, sm: 3 },
                  borderRadius: { xs: 2, sm: 3 },
                  border: '1px solid',
                  borderColor: 'error.main',
                  overflow: 'hidden'
                }}
                style={{ backgroundColor: alpha('#ef4444', 0.05) }}
              >
                <Stack direction="row" spacing={{ xs: 1.5, sm: 2 }} alignItems="flex-start">
                  <ErrorIcon color="error" sx={{ mt: 0.25, fontSize: { xs: 24, sm: 28 }, flexShrink: 0 }} />
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="subtitle1" color="error" fontWeight={700} sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                      {errorInfo ? errorInfo.title : (isMobile ? 'Erro' : 'Detalhes do erro')}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mt: 0.5,
                        wordBreak: 'break-word',
                        whiteSpace: 'pre-wrap',
                        fontSize: { xs: '0.75rem', sm: '0.875rem' }
                      }}
                    >
                      {errorInfo
                        ? errorInfo.detail
                        : (file?.errorMessage || analysisStatus?.errorMessage)}
                    </Typography>
                    {/* Se tem errorCode mas também mensagem técnica, exibir em seconário */}
                    {errorInfo && (file?.errorMessage || analysisStatus?.errorMessage) && (
                      <Typography
                        variant="caption"
                        color="text.disabled"
                        sx={{ mt: 1, display: 'block', fontFamily: 'monospace', fontSize: '0.7rem' }}
                      >
                        {file?.errorMessage || analysisStatus?.errorMessage}
                      </Typography>
                    )}
                  </Box>
                </Stack>
              </Paper>
            )}

            {/* Orientação para erros irrecuperáveis (sem áudio / sem fala) */}
            {isFailed && isUnrecoverableError && (
              <Paper
                elevation={0}
                sx={{
                  mt: { xs: 2, sm: 3 },
                  p: { xs: 2, sm: 3 },
                  borderRadius: { xs: 2, sm: 3 },
                  border: '1px solid',
                  borderColor: 'warning.light',
                }}
                style={{ backgroundColor: alpha('#f59e0b', 0.05) }}
              >
                <Stack direction="row" spacing={{ xs: 1.5, sm: 2 }} alignItems="flex-start">
                  <ReportProblemIcon sx={{ mt: 0.25, fontSize: { xs: 22, sm: 26 }, flexShrink: 0, color: 'warning.main' }} />
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="subtitle1" fontWeight={700} color="warning.dark" sx={{ fontSize: { xs: '0.85rem', sm: '0.95rem' } }}>
                      O que fazer?
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      Exclua este arquivo e envie uma nova gravação que contenha diálogos audivíveis. Certifique-se de que o volume está adequado e que não há apenas ruído de fundo.
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            )}

            {/* Botão de Retry */}
            {(showRetryOption || retrySuccess) && (
              <Paper
                elevation={0}
                sx={{
                  mt: { xs: 2, sm: 3 },
                  p: { xs: 2, sm: 3 },
                  borderRadius: { xs: 2, sm: 3 },
                  border: '1px solid',
                  borderColor: retrySuccess
                    ? 'success.main'
                    : isTimeout
                      ? 'warning.light'
                      : 'primary.light',
                  transition: 'all 0.4s ease-in-out',
                  ...(retrySuccess && {
                    animation: `${restartPulse} 1.5s ease-in-out`
                  })
                }}
                style={{
                  backgroundColor: retrySuccess
                    ? alpha('#10b981', 0.05)
                    : isTimeout
                      ? alpha('#f59e0b', 0.05)
                      : alpha('#6366f1', 0.03)
                }}
              >
                {/* Estado de sucesso do retry */}
                {retrySuccess ? (
                  <Box sx={{ animation: `${slideIn} 0.3s ease-out` }}>
                    <Stack direction="row" spacing={{ xs: 1.5, sm: 2 }} alignItems="center" mb={{ xs: 1.5, sm: 2 }}>
                      <Box
                        sx={{
                          width: { xs: 40, sm: 48 },
                          height: { xs: 40, sm: 48 },
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: alpha('#10b981', 0.15),
                          color: 'success.main',
                          flexShrink: 0,
                          animation: `${pulse} 1.5s ease-in-out infinite`
                        }}
                      >
                        <CheckCircleIcon sx={{ fontSize: { xs: 22, sm: 28 } }} />
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle1" fontWeight={700} color="success.main" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                          {isMobile ? 'Reiniciado!' : 'Reprocessamento Iniciado!'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                          {retryMessage || 'O processamento está sendo reiniciado...'}
                        </Typography>
                      </Box>
                    </Stack>

                    <Box sx={{
                      mt: { xs: 1.5, sm: 2 },
                      p: { xs: 1.5, sm: 2 },
                      borderRadius: 2,
                      bgcolor: alpha('#6366f1', 0.05),
                      border: '1px dashed',
                      borderColor: 'primary.light'
                    }}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <CircularProgress size={14} color="primary" />
                        <Typography variant="body2" color="primary.main" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                          {isMobile ? 'Atualizando...' : 'Aguarde... A página será atualizada automaticamente.'}
                        </Typography>
                      </Stack>
                    </Box>
                  </Box>
                ) : (
                  <>
                    <Typography variant="subtitle1" fontWeight={700} mb={{ xs: 1.5, sm: 2 }} sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                      {isTimeout ? (isMobile ? 'Lento' : 'Processamento Lento') : (isMobile ? 'Tentar de novo' : 'Tentar Novamente')}
                    </Typography>

                    <Typography variant="body2" color="text.secondary" mb={{ xs: 1.5, sm: 2 }} sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      {isTimeout ? (
                        hasTranscription
                          ? (isMobile
                            ? `Análise lenta (${minutesSinceTranscription} min). Reiniciar?`
                            : `A análise está demorando mais do que o esperado (${minutesSinceTranscription} minutos). Você pode tentar reiniciar a análise.`)
                          : (isMobile
                            ? `Processamento lento (${minutesSinceStart} min). Reprocessar?`
                            : `O processamento está demorando mais do que o esperado (${minutesSinceStart} minutos). Você pode tentar reprocessar o arquivo.`)
                      ) : (
                        hasTranscription
                          ? (isMobile
                            ? 'Erro na análise. Transcrição OK. Reanalisar?'
                            : 'A transcrição foi concluída, mas ocorreu um erro na análise. Você pode tentar reanalisar a transcrição existente.')
                          : (isMobile
                            ? 'Erro no processamento. Tentar de novo?'
                            : 'Ocorreu um erro durante o processamento. Você pode tentar reprocessar o arquivo do início.')
                      )}
                    </Typography>
                    
                    {/* Mensagem de sucesso */}
                    {retryMessage && !retryError && (
                      <Paper
                        elevation={0}
                        sx={{
                          p: { xs: 1.5, sm: 2 },
                          mb: { xs: 1.5, sm: 2 },
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: 'success.light',
                          animation: `${fadeIn} 0.3s ease-out`
                        }}
                        style={{ backgroundColor: alpha('#10b981', 0.1) }}
                      >
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <CheckCircleIcon color="success" sx={{ fontSize: { xs: 18, sm: 20 } }} />
                          <Typography variant="body2" color="success.main" fontWeight={500} sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                            {retryMessage}
                          </Typography>
                        </Stack>
                      </Paper>
                    )}

                    {/* Mensagem de erro */}
                    {retryError && (
                      <Paper
                        elevation={0}
                        sx={{
                          p: { xs: 1.5, sm: 2 },
                          mb: { xs: 1.5, sm: 2 },
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: 'error.light',
                          animation: `${fadeIn} 0.3s ease-out`
                        }}
                        style={{ backgroundColor: alpha('#ef4444', 0.1) }}
                      >
                        <Stack direction="row" alignItems="flex-start" spacing={1}>
                          <ErrorIcon color="error" sx={{ fontSize: { xs: 18, sm: 20 }, mt: 0.25, flexShrink: 0 }} />
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" color="error.main" fontWeight={600} sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                              {isMobile ? 'Falha' : 'Falha ao tentar novamente'}
                            </Typography>
                            <Typography variant="body2" color="error.main" sx={{ mt: 0.5, fontSize: { xs: '0.7rem', sm: '0.875rem' }, wordBreak: 'break-word' }}>
                              {retryError}
                            </Typography>
                          </Box>
                        </Stack>
                      </Paper>
                    )}

                    <Button
                      variant="contained"
                      color={retryError ? 'error' : isTimeout ? 'warning' : 'primary'}
                      startIcon={retrying ? <CircularProgress size={16} color="inherit" /> : <ReplayIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />}
                      onClick={handleRetry}
                      disabled={retrying}
                      fullWidth
                      sx={{
                        py: { xs: 1, sm: 1.5 },
                        fontSize: { xs: '0.8rem', sm: '0.875rem' },
                        transition: 'all 0.3s ease',
                        backgroundImage: retryError
                          ? 'linear-gradient(135deg, #ef4444 0%, #f87171 100%)'
                          : isTimeout
                            ? 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)'
                            : 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                        '&:hover': {
                          boxShadow: 3
                        },
                        '&:disabled': {
                          backgroundImage: 'linear-gradient(135deg, #9ca3af 0%, #d1d5db 100%)'
                        }
                      }}
                    >
                      {retrying
                        ? (isMobile ? 'Reiniciando...' : 'Reiniciando...')
                        : retryError
                          ? (isMobile ? 'Tentar' : 'Tentar Novamente')
                          : hasTranscription
                            ? (isMobile ? 'Refazer' : 'Refazer Análise')
                            : (isMobile ? 'Reprocessar' : 'Reprocessar Arquivo')
                      }
                    </Button>
                  </>
                )}
              </Paper>
            )}
          </Grid>

          {/* Coluna direita - Status das análises */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2, sm: 3 },
                borderRadius: { xs: 2, sm: 3 },
                border: '1px solid',
                borderColor: 'divider'
              }}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between" mb={{ xs: 1.5, sm: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                    {isMobile ? 'Análises' : 'Status das Análises'}
                  </Typography>
                  {hasAnalysisConfig && (
                    <Chip
                      size="small"
                      label={`${selectedAnalyses!.length}/6`}
                      variant="outlined"
                      sx={{
                        height: 20,
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        borderColor: alpha('#6366f1', 0.3),
                        color: 'text.secondary'
                      }}
                    />
                  )}
                </Stack>
                <IconButton
                  size="small"
                  onClick={() => setShowDetails(!showDetails)}
                >
                  {showDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Stack>

              <Collapse in={showDetails}>
                <Stack spacing={{ xs: 1, sm: 2 }}>
                  <AnalysisStatusItem
                    label="Análise de Sentimento"
                    status={getItemStatus('sentiment', analysisStatus?.sentiment)}
                    icon={<SentimentSatisfiedIcon />}
                    description="Detecta emoções e tom do diálogo"
                  />
                  <AnalysisStatusItem
                    label="Extração de Entidades"
                    status={getItemStatus('entities', analysisStatus?.entities)}
                    icon={<LabelIcon />}
                    description="Identifica pessoas, locais e organizações"
                  />
                  <AnalysisStatusItem
                    label="Detecção de Tópicos"
                    status={getItemStatus('topics', analysisStatus?.topics)}
                    icon={<TopicIcon />}
                    description="Categoriza os assuntos abordados"
                  />
                  <AnalysisStatusItem
                    label="Timeline de Eventos"
                    status={getItemStatus('timeline', analysisStatus?.timeline)}
                    icon={<TimelineIcon />}
                    description="Organiza eventos cronologicamente"
                  />
                  <AnalysisStatusItem
                    label="Padrões de Fala"
                    status={getItemStatus('patterns', analysisStatus?.patterns)}
                    icon={<RecordVoiceOverIcon />}
                    description="Analisa estilos de comunicação"
                  />
                  <AnalysisStatusItem
                    label="Resumo com IA"
                    status={getItemStatus('summary', analysisStatus?.summary)}
                    icon={<SummarizeIcon />}
                    description="Gera resumo inteligente do conteúdo"
                  />
                </Stack>
              </Collapse>
            </Paper>
          </Grid>
        </Grid>

        {/* Ações no rodapé */}
        <Paper
          elevation={0}
          sx={{
            mt: { xs: 2, sm: 4 },
            p: { xs: 2, sm: 3 },
            borderRadius: { xs: 2, sm: 3 },
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper'
          }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', sm: 'center' }}
            spacing={{ xs: 2, sm: 0 }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                textAlign: { xs: 'center', sm: 'left' }
              }}
            >
              {isFailed
                ? (isMobile ? 'Falhou. Tente novamente ou exclua.' : 'O processamento falhou. Você pode tentar novamente ou excluir o arquivo.')
                : isCompleted
                  ? (isMobile ? 'Concluído! Volte para ver.' : 'O processamento foi concluído. Volte para a lista para visualizar a análise.')
                  : (isMobile ? 'Processando... Atualiza automaticamente.' : 'O processamento está em andamento. Esta página atualiza automaticamente.')}
            </Typography>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={{ xs: 1, sm: 2 }}
            >
              {isCompleted && onOpenAnalysis ? (
                // Botão chamativo quando concluído
                <>
                  <Button
                    variant="outlined"
                    color="inherit"
                    onClick={onBack}
                    startIcon={<ArrowBackIcon />}
                    size={isMobile ? 'medium' : 'medium'}
                    sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                  >
                    {isMobile ? 'Voltar' : 'Voltar'}
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    onClick={onOpenAnalysis}
                    startIcon={<VisibilityIcon />}
                    endIcon={<PlayArrowIcon />}
                    fullWidth={isMobile}
                    sx={{
                      fontSize: { xs: '1rem', sm: '1.1rem' },
                      fontWeight: 600,
                      py: { xs: 1.5, sm: 2 },
                      px: { xs: 3, sm: 4 },
                      background: 'linear-gradient(90deg, #6366f1 0%, #8b5cf6 50%, #6366f1 100%)',
                      backgroundSize: '200% auto',
                      animation: `${shimmer} 3s linear infinite`,
                      boxShadow: '0 4px 20px rgba(99, 102, 241, 0.4)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        boxShadow: '0 6px 30px rgba(99, 102, 241, 0.6)',
                        transform: 'translateY(-2px) scale(1.02)'
                      },
                      '&:active': {
                        transform: 'translateY(0) scale(0.98)'
                      }
                    }}
                  >
                    {isMobile ? 'Abrir Análise' : 'Abrir Análise Completa'}
                  </Button>
                </>
              ) : (
                // Botões normais quando não concluído
                <>
                  <Button
                    variant="outlined"
                    color="inherit"
                    onClick={onBack}
                    startIcon={<ArrowBackIcon />}
                    size={isMobile ? 'medium' : 'medium'}
                    fullWidth={isMobile}
                    sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                  >
                    {isMobile ? 'Voltar' : 'Voltar para Lista'}
                  </Button>
                  {isFailed && (
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleRefresh}
                      startIcon={<RefreshIcon />}
                      fullWidth={isMobile}
                      sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                    >
                      {isMobile ? 'Tentar' : 'Tentar Novamente'}
                    </Button>
                  )}
                </>
              )}
            </Stack>
          </Stack>
        </Paper>
      </Container>

      {/* Modal de Confirmação de Reprocessamento */}
      <ConfirmRetryDialog
        open={showRetryDialog}
        onClose={() => setShowRetryDialog(false)}
        onConfirm={handleConfirmRetry}
        retryType={autoRetryType}
        fileName={file?.title}
        isRetrying={retrying}
      />

      {/* Modal de Confirmação de Exclusão */}
      <ConfirmDeleteDialog
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleConfirmDelete}
        itemType="file"
        itemName={file?.title}
        isDeleting={isDeleting}
      />
    </Box>
  )
}
